import React from "react"
import PropTypes from "prop-types"
import { withRadium, DetectCompact } from "higher-order-components"

/**
 * Renders full width grid cell
 * @param {Object} props - Component of props.
 * @param {Array} props.items - Array of components to render inside grid cell.
 * @returns {ReactElement} React Component
 */
const fullWidth = ({ items }) => {
  if (items.length < 1) return
  return items.splice(0, 1).map(wrapItems({
    additionalStyles: {
      paddingBottom: "50%"
    }
  }))
}

fullWidth.propTypes = {
  items: PropTypes.array
}

/**
 * Renders one large cell with two supporting cells
 * @param {Object} props - Component of props.
 * @param {Array} props.items - Array of components to render inside grid cell.
 * @param {boolean} left - Render the feature image on the left.
 * @returns {ReactElement} React Component
 */
const featureTwoSupport = ({ items, left }) => {
  if (items.length < 3) return
  const dimension = 100 / items.length
  const largeItemProps = {
    additionalStyles: {
      flex: `1 1 ${2 * dimension}%`
    }
  }
  return (
    <div style={{ display: "flex" }}>
      {left &&
        items.splice(0, 1).map(firefix).map(wrapItems(largeItemProps))}
      <div
        style={{
          display: "flex",
          flex: `1 1 ${dimension}%`,
          flexDirection: "column"
        }}>
        {items.splice(0, 1).map(wrapItems())}
        {items.splice(0, 1).map(wrapItems())}
      </div>
      {!left &&
        items.splice(0, 1).map(firefix).map(wrapItems(largeItemProps))}
    </div>
  )
}

featureTwoSupport.propTypes = {
  items: PropTypes.array,
  left: PropTypes.bool
}

/**
 * Renders a row of equi dimensioned cells.
 * @param {Object} props - Component of props.
 * @param {Array} props.items - Array of components to render inside grid cell.
 * @returns {ReactElement} React Component
 */
const imageRow = ({ items }) => {
  if (items.length < 2) return fullWidth({ items: items })
  const dimension = 100 / items.length
  return (
    <div style={{ display: "flex" }}>
      {items.map(firefix).map(wrapItems({
        additionalStyles: {
          flex: `1 1 ${dimension}%`
        }
      }))}
    </div>
  )
}

// Fix for SM-2915 - Firefox not calculating height of flex if all GalleryItems are position: absolute
const firefix = item => React.cloneElement(item, { additionalStyles: { position: "relative" } })

imageRow.propTypes = {
  items: PropTypes.array
}

/**
 * Assign components to grid function based on position in array & responsive state.
 * @param {Object} props - Component of props.
 * @param {Array} props.items - Array of components to render inside grid cell.
 * @param {Object} props.additionalStyles - Styles to add to div wrapping component.
 * @returns {ReactElement} React Component
 */
const wrapItems = ({ additionalStyles = {} } = {}) => (item, index) => (
  <div
    key={index}
    style={[
      {
        position: "relative",
        flex: "1 1 auto",
        overflow: "hidden",
        userSelect: "none",
        border: "3px solid transparent"
      },
      // If the item is a normal GalleryItem, put in additional styles
      // otherwise (e.g. ImagesRotator) we don't need those styles
      item.type.displayName === "GalleryItem" && additionalStyles
    ]}>
    {item}
  </div>
)


/** Render a gallery grid.
 * @param {Object} props - Component of props.
 * @param {Array|GridItem} props.components - Array of GridItem components.
 * @param {Boolean} props.compact - Render component as compact.
 * @param {Array|Layout String} layouts - Array of Layouts.
 * @returns {ReactElement} React Component
 */
const GalleryGrid = ({ children, compact, layouts }) => {
  let newLayouts = layouts
  const items = children.slice(0)
  if (!newLayouts || !newLayouts.length) {
    newLayouts = ["Large", "MediumLeftSmall", "Small", "SmallMediumRight", "Small"]
  }

  if (!compact) {
    return (
      <div style={{ margin: "2.5em 0 1em" }}>
        {items.length && newLayouts.map((layout, index) => (
          <div key={index}>
            {
              layout === "Small" ? imageRow({ items: items.splice(0, 3) }) :
                (layout === "MediumLeftSmall" ? featureTwoSupport({ items: items.splice(0, 3), left: true }) :
                  (layout === "SmallMediumRight" ? featureTwoSupport({ items: items.splice(0, 3), left: false }) :
                    fullWidth({ items: items.splice(0, 1) })))
            }
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ margin: "2.5em 0 1em" }}>
      {items && items.length && newLayouts.map((layout, index) => (
        <div key={index}>
          {
            layout === "Large" ?
            fullWidth({ items: items.splice(0, 1) })
            :
            imageRow({ items: items.splice(0, 2) })
          }
        </div>
      ))}
    </div>
  )
}

GalleryGrid.propTypes = {
  compact: PropTypes.bool,
  children: PropTypes.node,
  layouts: PropTypes.array
}

export default withRadium(DetectCompact())(GalleryGrid)



// WEBPACK FOOTER //
// ./src/containers/gallery-grid/index.jsx